import { Box } from "@mui/material";
import type { ChartsItemContentProps } from "@mui/x-charts/ChartsTooltip";

import { ds } from "../../theme/ds";

// The design system's chart tooltip (design-system.html, Charts: "white
// background, navy text, 1px gray-200 border, light shadow, shows series +
// value"), at the chart's own 11px type. For a single-series bar chart the
// useful pair is the bar's label and its value, so the content is built per
// chart with the axis labels and the value formatter in a closure. The
// library's default is a padded paper in 16px body type showing the series
// name; Bill saw it as a huge card (2026-09-08).
export function makeItemTooltip(labels: string[], print: (v: number | null) => string) {
    return function ItemTooltip({ itemData, series }: ChartsItemContentProps<"bar">) {
        const i = itemData.dataIndex ?? 0;
        const value = (series.data[i] ?? null) as number | null;
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "8px",
                    p: "4px 8px",
                    bgcolor: ds.white,
                    border: `1px solid ${ds.gray200}`,
                    borderRadius: "6px",
                    boxShadow: ds.shadowSm,
                    fontSize: ds.text.xs,
                    lineHeight: 1.4,
                    color: ds.navy,
                    whiteSpace: "nowrap",
                }}
            >
                <Box component="span" sx={{ color: ds.gray600 }}>
                    {labels[i] ?? series.label}
                </Box>
                <Box component="span" sx={{ fontWeight: 700 }}>
                    {print(value)}
                </Box>
            </Box>
        );
    };
}

// The library drops its own paper when the content slot is customised, so the
// frame above lives on the content; this only strips any wrapper padding.
// Pass as `slotProps.popper`.
export const tooltipPopperProps = {
    sx: {
        "& .MuiChartsTooltip-paper": {
            bgcolor: ds.white,
            color: ds.navy,
            border: `1px solid ${ds.gray200}`,
            borderRadius: "6px",
            boxShadow: ds.shadowSm,
            p: 0,
            fontSize: ds.text.xs,
        },
    },
} as const;
